import { MergedApi } from './server';
import client from '@/api/server';
import { TVGuideFeedQuery, TVGuideDStvQuery } from '@/types/tvGuide';
import { RawApi } from '.';
import { SportSummaryType, Summary as ScoreSummary, SportEvent } from '@/components/Sport/types';
import { Summary, Video, Article, Country } from './validators/content';
import { Feed } from './validators/feed';
import {
  FootballTable,
  FootballLineUp,
} from './validators/football_logs';
import { RugbyTable, RugbyMatchDetail } from './validators/rugby_logs';
import { FootballTopScorers } from './validators/football_top_scorers';
import { TennisPlayersInfo, TennisRankings } from './validators/tennis';
import { CricketTable } from './validators/cricket_logs';
import { GolfRankingId } from './validators/golf';

class CacheReaderApi {
  protected readonly feedPerTag: { [key: string]: Feed } = {};
  protected readonly summaries: { [key: string]: Summary } = {};
  protected readonly scoreSummaries: { [key: string]: ScoreSummary } = {};
  protected readonly videos: { [key: string]: Video } = {};
  protected readonly articles: { [key: string]: Article } = {};
  protected readonly footballTable: { [key: string]: FootballTable } = {};
  protected readonly footballTopScorers: { [key: string]: FootballTopScorers } = {};
  protected readonly rugbyTable: { [key: string]: RugbyTable } = {};
  protected readonly footballLineups: { [key: string]: FootballLineUp } = {};
  protected readonly rugbyMatchDetails: { [key: number]: RugbyMatchDetail } = {};
  protected readonly sportEvents: { [key: string]: SportEvent } = {};
  protected readonly tennisPlayersInfo: {[key: string]: TennisPlayersInfo} = {};
  protected readonly cricketTable: { [key: string]: CricketTable } = {};
  protected readonly tennisRankings: { [key: string]: TennisRankings } = {};
  protected countries: Country[] = [];

  public readArticle(id: string) {
    return this.articles[id];
  }

  public readContentSummary(id: string) {
    return this.summaries[id];
  }

  public readScoreSummary(id: string) {
    return this.scoreSummaries[id];
  }

  public readCricketMatchScorecard(eventId: string): SportEvent {
    return this.sportEvents[eventId];
  }

  public readTennisPlayersDetails(eventId: string): TennisPlayersInfo {
    return this.tennisPlayersInfo[eventId];
  }

  public readVideo(id: string) {
    return this.videos[id];
  }

  public readFeed(feedKey: string) {
    return this.feedPerTag[feedKey];
  }

  public readFootballLogs(tournamentId: string) {
    return this.footballTable[tournamentId];
  }

  public readFootballTopScorers(tournamentId: string) {
    return this.footballTopScorers[tournamentId];
  }

  public readRugbyLogs(slug: string) {
    return this.rugbyTable[slug];
  }

  public readRugbyMatchDetails(slug: number) {
    return this.rugbyMatchDetails[slug];
  }

  public readFootballLineups(eventId: string) {
    return this.footballLineups[eventId];
  }

  public readCricketLogs(slug: string) {
    return this.cricketTable[slug];
  }

  public readTennisRankings(id: string) {
    return this.tennisRankings[id];
  }

  public readCountries() {
    return this.countries;
  }
}

class CacheWriter extends CacheReaderApi {
  public writeArticle(id: string, article: Article) {
    this.articles[id] = article;
  }

  public writeContentSummary(id: string, summary: Summary) {
    this.summaries[id] = summary;
  }

  public writeScoreSummary(id: string, summary: ScoreSummary) {
    this.scoreSummaries[id] = summary;
  }

  public writeCricketMatchScorecard(eventId: string, scorecard: SportEvent) {
    this.sportEvents[eventId] = scorecard;
  }

  public writeTennisPlayersDetails(eventId: string, player: TennisPlayersInfo) {
    this.tennisPlayersInfo[eventId] = player;
  }

  public writeVideo(id: string, video: Video) {
    this.videos[id] = video;
  }

  public writeFeed(feedKey: string, feed: Feed) {
    this.feedPerTag[feedKey] = feed;
  }

  public writeFootballLogs(tournamentId: string, table: FootballTable) {
    this.footballTable[tournamentId] = table;
  }

  public writeFootballTopScorers(tournamentId: string, scorers: FootballTopScorers) {
    this.footballTopScorers[tournamentId] = scorers;
 }

  public writeFootballLineups(id: string, lineup: FootballLineUp) {
    this.footballLineups[id] = lineup;
  }

  public writeRugbyLogs(slug: string, table: RugbyTable) {
    this.rugbyTable[slug] = table;
  }

  public writeRugbyMatchDetails(slug: number, matchDetails: RugbyMatchDetail) {
    this.rugbyMatchDetails[slug] = matchDetails;
  }

  public writeCricketLogs(slug: string, table: CricketTable) {
    this.cricketTable[slug] = table;
  }

  public writeTennisRankings(id: string, rankings: TennisRankings) {
    this.tennisRankings[id] = rankings;
  }

  public writeCountries(countries: Country[]) {
    this.countries = countries;
  }
}

export class CachedApi extends RawApi implements MergedApi {
  public readonly cache: CacheWriter = new CacheWriter();

  public async fetchNavigation() {
    return client.fetchNavigation();
  }

  public async fetchFeedTvGuide(query: TVGuideFeedQuery) {
    return client.fetchFeedTvGuide(query);
  }

  public async fetchDStvTvGuide(query: TVGuideDStvQuery) {
    return client.fetchDStvTvGuide(query);
  }

  public async fetchArticle(id: string) {
    const article = this.cache.readArticle(id) ?? await client.fetchArticle(id);
    this.cache.writeArticle(id, article);
    return article;
  }

  public async fetchContentSummary(type: 'articles' | 'video', id: string) {
    const summary = this.cache.readContentSummary(id) ?? await client.fetchContentSummary(type, id);
    this.cache.writeContentSummary(id, summary);
    return summary;
  }

  public async fetchRelatedContent(type: 'articles' | 'video', categoryId: string) {
    // Do not cache or add timed caching
    return client.fetchRelatedContent(type, categoryId);
  }

  public async fetchScoreSummary(sport: SportSummaryType, id: string, itemSubType: string | undefined) {
    const summary = this.cache.readScoreSummary(id) ?? await client.fetchScoreSummary(sport, id, itemSubType);
    this.cache.writeScoreSummary(id, summary);
    return summary;
  }

  public async fetchCricketMatchScorecard(
    eventId: string,
  ): Promise<SportEvent> {
    const scorecard = this.cache.readCricketMatchScorecard(eventId) ?? await client.fetchCricketMatchScorecard(eventId);
    this.cache.writeCricketMatchScorecard(eventId, scorecard);
    return scorecard;
  }

  public async fetchTennisPlayersDetails(tourId: string, eventId: string): Promise<TennisPlayersInfo> {
    const player = this.cache.readTennisPlayersDetails(eventId) ?? await client.fetchTennisPlayersDetails(tourId, eventId);
    this.cache.writeTennisPlayersDetails(eventId, player);
    return player;
  }

  public async fetchVideo(id: string) {
    const video = this.cache.readVideo(id) ?? await client.fetchVideo(id);
    this.cache.writeVideo(id, video);
    return video;
  }

  public async fetchFeed(
    type: string,
    entityTagIds: string[],
    pageSize: number,
    optionalArgs?: object,
    continuationToken?: string,
  ) {
    const feedKey = this.getFeedKey(arguments);
    const feed = this.cache.readFeed(feedKey) ?? await client.fetchFeed(type, entityTagIds, pageSize, optionalArgs, continuationToken);
    this.cache.writeFeed(feedKey, feed);
    return feed;
  }

  public async fetchFootballLogs(tournamentId: string) {
    const table = this.cache.readFootballLogs(tournamentId) ?? await client.fetchFootballLogs(tournamentId);
    this.cache.writeFootballLogs(tournamentId, table);
    return table;
  }

  public async fetchFootballTopScorers(tournamentId: string) {
    const scorers = this.cache.readFootballTopScorers(tournamentId) ?? await client.fetchFootballTopScorers(tournamentId);
    this.cache.writeFootballTopScorers(tournamentId, scorers);
    return scorers;
 }

  public async fetchRugbyLogs(slug: string) {
    const table = this.cache.readRugbyLogs(slug) ?? await client.fetchRugbyLogs(slug);
    this.cache.writeRugbyLogs(slug, table);
    return table;
  }

  public async fetchRugbyMatchDetails(slug: number) {
    const matchDetails = this.cache.readRugbyMatchDetails(slug) ?? await client.fetchRugbyMatchDetails(slug);
    this.cache.writeRugbyMatchDetails(slug, matchDetails);
    return matchDetails;
  }

  public async fetchCricketLogs(slug: string) {
    const table = this.cache.readCricketLogs(slug) ?? await client.fetchCricketLogs(slug);
    this.cache.writeCricketLogs(slug, table);
    return table;
  }

  public async fetchCountries() {
    const countries = this.cache.readCountries() ?? await client.fetchCountries();
    this.cache.writeCountries(countries);
    return countries;
  }

  public async fetchMotorsportRaceGrid(tourId: string, eventId: string) {
    return client.fetchMotorsportRaceGrid(tourId, eventId);
  }

  public async fetchGolfLeaderboard(tourId: string, eventId: string) {
    return client.fetchGolfLeaderboard(tourId, eventId);
  }

  public async fetchGolfMatchplayLeaderboard(tourId: string, eventId: string) {
    return client.fetchGolfMatchplayLeaderboard(tourId, eventId);
  }

  public async fetchFootballLineUp(leaguePath: string, eventId: string) {
    const lineups = this.cache.readFootballLineups(leaguePath + eventId) ?? await client.fetchFootballLineUp(leaguePath, eventId);
    this.cache.writeFootballLineups(leaguePath + eventId, lineups);
    return lineups;
  }

  public async fetchFootballCommentary(leaguePath: string, eventId: string) {
    return client.fetchFootballCommentary(leaguePath, eventId);
  }

  public async fetchFootballStats(leaguePath: string, eventId: string) {
    return client.fetchFootballStats(leaguePath, eventId);
  }

  public async fetchTennisRankings(slug: string, type: 'single' | 'race') {
    const rankings = this.cache.readTennisRankings(slug + type) ?? await client.fetchTennisRankings(slug, type);
    this.cache.writeTennisRankings(slug + type, rankings);
    return rankings;
  }

  public async fetchMotorsportDriverStandings(tourId: string) {
    return client.fetchMotorsportDriverStandings(tourId);
  }

  public async fetchMotorsportTeamStandings(tourId: string) {
    return client.fetchMotorsportTeamStandings(tourId);
  }

  public async fetchTennisMatchResults(tourId: string, eventId: string) {
    return client.fetchTennisMatchResults(tourId, eventId);
  }
  public async fetchGolfRanking(tourId: string, rankingId: GolfRankingId) {
    return client.fetchGolfRanking(tourId, rankingId);
  }

  private getFeedKey(args: IArguments) {
    return JSON.stringify(args);
  }
}

const cachedApi = new CachedApi();

export default cachedApi;
